import { useActionFailedAlert } from "shared/problems/composables/actionFailedAlert";
import { createCompletionSource } from "shared/promise";
import { ref } from "vue";
import type { ActionResponse, SuccessResponse } from "../api/httpClient";

export interface RefreshStoreOptions<T extends SuccessResponse> {
  fetchFromServer: () => Promise<ActionResponse<T>>;
  mergeToStore: (response: T) => void;
}

export function useRefreshStore<T extends SuccessResponse>(options: RefreshStoreOptions<T>) {
  const { fetchFromServer, mergeToStore } = options;

  const initialized = ref(false);
  const loading = ref(false);
  const actionFailedAlert = useActionFailedAlert();

  let refreshOperation = createCompletionSource<void>();
  refreshOperation.resolve();

  async function initialize() {
    if (initialized.value) return;
    await refresh();
  }

  async function refresh() {
    if (!refreshOperation.completed) return refreshOperation.promise;
    try {
      loading.value = true;
      refreshOperation = createCompletionSource();

      const response = await fetchFromServer();

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        refreshOperation.resolve();
        return;
      }

      mergeToStore(response);

      initialized.value = true;
      refreshOperation.resolve();
    } catch(err) {
      refreshOperation.reject(err);
    } finally {
      loading.value = false;
    }
  }

  return {
    initialize,
    refresh,
    loading
  };
}
